import React, { useState, useEffect, useContext } from "react";

// ######### MATERIAL UI
import { makeStyles } from "@material-ui/core";

// ######### CUSTOM COMPONENTS
import Card from "components/Card/Card";
import CardHeader from "components/Card/CardHeader";
import CardIcon from "components/Card/CardIcon";
import SettingsIcon from "@material-ui/icons/Settings";

// ######### INTERNAL COMPONENTS
import PanelInputs from "./viewComponents/PanelInputs/PanelInputs";
import DrawerFileIngestion from "./viewComponents/DrawerFileIngestion/DrawerFileIngestion";

import {
  getInputMetadata,
  getInputColumns,
  getInputFiles,
  getIngestionColumns,
} from "./viewServices/apiCalls";

// ######### AUTH
import AuthContext from "connections/auth/AuthProvider.js";
import { useTranslation } from "react-i18next";

// ######### STYLES
const inputsStyle = (theme) => ({
  cardHeaderCard: {
    marginTop: theme.spacing(-2),
    marginBottom: theme.spacing(1),
  },
  cardHeaderTitle: {
    ...theme.typography.h6,
    fontSize: "1.125rem",
    color: theme.palette.secondary.main,
    fontWeight: "400",
    marginBottom: "10px",
    marginTop: "10px",
  },
});

const useStyles = makeStyles(inputsStyle);

// ######### COMPONENT
export default function InputsView() {
  const { userDetails } = useContext(AuthContext);
  const classes = useStyles({ userDetails });

  const T_PREFIX = "rgm.inputs";
  const { t } = useTranslation("translation", { keyPrefix: T_PREFIX });

  // ######### GLOBAL STATES
  const [inputMetadata, setInputMetadata] = useState({});
  const [inputColumns, setInputColumns] = useState({});
  const [inputFiles, setInputFiles] = useState({});
  const [ingestionColumns, setIngestionColumns] = useState({});
  const [isLoadingMetadata, setIsLoadingMetadata] = useState(true);
  const [isLoadingColumns, setIsLoadingColumns] = useState(true);

  // ######### DRAWER CONTROL
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedInputTable, setSelectedInputTable] = useState("");

  const handleSelectTable = (tableId) => {
    setSelectedInputTable(tableId);
    setDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setSelectedInputTable("");
  };

  // ######### LOAD FUNCTIONS
  const loadMetadata = async () => {
    setIsLoadingMetadata(true);
    try {
      const metadata = await getInputMetadata();
      setInputMetadata(metadata);
    } catch (err) {
      console.error("Failed to fetch input metadata", err);
    } finally {
      setIsLoadingMetadata(false);
    }
  };

  const loadColumnsAndFiles = async () => {
    setIsLoadingColumns(true);
    try {
      const [columns, files, ingestionCols] = await Promise.all([
        getInputColumns(),
        getInputFiles(),
        getIngestionColumns(),
      ]);
      setInputColumns(columns);
      setInputFiles(files);
      setIngestionColumns(ingestionCols);
    } catch (err) {
      console.error("Failed to fetch input columns or files", err);
    } finally {
      setIsLoadingColumns(false);
    }
  };

  // ######### INITIAL LOAD
  useEffect(() => {
    loadMetadata();
    loadColumnsAndFiles();
  }, []);

  // ######### RETURN VIEW
  return (
    <div>
      <Card className={classes.cardHeaderCard}>
        <CardHeader color="client" icon>
          <CardIcon color="client">
            <SettingsIcon />
          </CardIcon>
          <h4 className={classes.cardHeaderTitle}>{t("title")}</h4>
        </CardHeader>
      </Card>
      <PanelInputs
        selectedInputTable={selectedInputTable}
        setSelectedInputTable={handleSelectTable}
        inputMetadata={inputMetadata}
        inputColumns={inputColumns}
        isLoadingMetadata={isLoadingMetadata}
      />

      <DrawerFileIngestion
        open={drawerOpen}
        onClose={handleCloseDrawer}
        selectedInputTable={selectedInputTable}
        inputMetadata={inputMetadata}
        inputColumns={inputColumns}
        inputFiles={inputFiles}
        ingestionColumns={ingestionColumns}
        isLoadingMetadata={isLoadingMetadata}
        isLoadingColumns={isLoadingColumns}
        onMetadataRefresh={loadMetadata}
        onColumnsRefresh={loadColumnsAndFiles}
        userDetails={userDetails}
      />
    </div>
  );
}
